import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";

export type SkillRegistryRow = Database["public"]["Tables"]["skill_registry"]["Row"];
export type SkillGoldenCase = Database["public"]["Tables"]["skill_golden_cases"]["Row"];
export type SkillGoldenRun = Database["public"]["Tables"]["skill_golden_runs"]["Row"];
export type SkillFailureLog = Database["public"]["Tables"]["skill_failure_log"]["Row"];
export type SkillExecution = Database["public"]["Tables"]["skill_executions"]["Row"];
export type SkillRollbackEvent = Database["public"]["Tables"]["skill_rollback_events"]["Row"];

/** Single skill */
export function useSkillById(id: string | undefined) {
  return useQuery({
    queryKey: ["skill-detail", "skill", id],
    enabled: !!id,
    queryFn: async (): Promise<SkillRegistryRow | null> => {
      const { data, error } = await supabase.from("skill_registry").select("*").eq("id", id!).maybeSingle();
      if (error) throw new Error(error.message);
      return data;
    },
  });
}

/** Update YAML spec (and any editable fields) */
export function useUpdateSkill() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      patch,
    }: {
      id: string;
      patch: Partial<Database["public"]["Tables"]["skill_registry"]["Update"]>;
    }) => {
      const { error } = await supabase.from("skill_registry").update(patch).eq("id", id);
      if (error) throw new Error(error.message);
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["skill-detail", "skill", vars.id] });
      qc.invalidateQueries({ queryKey: ["skill-os", "skills"] });
    },
  });
}

/** Compile root template + yaml_spec → preview system_prompt */
export function useCompiledPrompt(skillId: string | undefined) {
  return useQuery({
    queryKey: ["skill-detail", "compiled", skillId],
    enabled: !!skillId,
    queryFn: async (): Promise<{ system_prompt: string; warnings: string[] }> => {
      const { data, error } = await supabase.functions.invoke("skill-root-template-compile", {
        body: { skill_id: skillId },
      });
      if (error) throw new Error(error.message);
      return data as { system_prompt: string; warnings: string[] };
    },
  });
}

/** Golden cases for a skill */
export function useSkillGoldenCases(skillId: string | undefined) {
  return useQuery({
    queryKey: ["skill-detail", "golden-cases", skillId],
    enabled: !!skillId,
    queryFn: async (): Promise<SkillGoldenCase[]> => {
      const { data, error } = await supabase
        .from("skill_golden_cases")
        .select("*")
        .eq("skill_id", skillId!)
        .order("created_at", { ascending: false });
      if (error) throw new Error(error.message);
      return data ?? [];
    },
  });
}

/** Latest golden runs for a skill */
export function useSkillGoldenRuns(skillId: string | undefined, limit = 50) {
  return useQuery({
    queryKey: ["skill-detail", "golden-runs", skillId, limit],
    enabled: !!skillId,
    queryFn: async (): Promise<SkillGoldenRun[]> => {
      const { data, error } = await supabase
        .from("skill_golden_runs")
        .select("*")
        .eq("skill_id", skillId!)
        .order("created_at", { ascending: false })
        .limit(limit);
      if (error) throw new Error(error.message);
      return data ?? [];
    },
  });
}

/** Add a golden case */
export function useAddGoldenCase() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Database["public"]["Tables"]["skill_golden_cases"]["Insert"]) => {
      const { error } = await supabase.from("skill_golden_cases").insert(payload);
      if (error) throw new Error(error.message);
    },
    onSuccess: (_d, vars) =>
      qc.invalidateQueries({ queryKey: ["skill-detail", "golden-cases", vars.skill_id] }),
  });
}

/** Run golden suite for a skill */
export function useRunGolden() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ skill_id }: { skill_id: string }) => {
      const { data, error } = await supabase.functions.invoke("skill-golden-runner", {
        body: { skill_id },
      });
      if (error) throw new Error(error.message);
      return data as { ok: boolean; total: number; passed: number; failed: number; pass_rate: number };
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["skill-detail", "golden-runs", vars.skill_id] });
    },
  });
}

/** Recent executions for a skill (last 24h) */
export function useSkillExecutions(skillId: string | undefined, hours = 24) {
  return useQuery({
    queryKey: ["skill-detail", "executions", skillId, hours],
    enabled: !!skillId,
    queryFn: async (): Promise<SkillExecution[]> => {
      const since = new Date(Date.now() - hours * 3600_000).toISOString();
      const { data, error } = await supabase
        .from("skill_executions")
        .select("*")
        .eq("skill_id", skillId!)
        .gte("created_at", since)
        .order("created_at", { ascending: false })
        .limit(200);
      if (error) throw new Error(error.message);
      return data ?? [];
    },
    refetchInterval: 30_000,
  });
}

/** Recent failures for a skill */
export function useSkillFailures(skillId: string | undefined, hours = 24) {
  return useQuery({
    queryKey: ["skill-detail", "failures", skillId, hours],
    enabled: !!skillId,
    queryFn: async (): Promise<SkillFailureLog[]> => {
      const since = new Date(Date.now() - hours * 3600_000).toISOString();
      const { data, error } = await supabase
        .from("skill_failure_log")
        .select("*")
        .eq("skill_id", skillId!)
        .gte("created_at", since)
        .order("created_at", { ascending: false })
        .limit(100);
      if (error) throw new Error(error.message);
      return data ?? [];
    },
    refetchInterval: 30_000,
  });
}

/** Recent rollback events for a skill */
export function useSkillRollbackEvents(skillId: string | undefined) {
  return useQuery({
    queryKey: ["skill-detail", "rollback-events", skillId],
    enabled: !!skillId,
    queryFn: async (): Promise<SkillRollbackEvent[]> => {
      const { data, error } = await supabase
        .from("skill_rollback_events")
        .select("*")
        .eq("skill_id", skillId!)
        .order("created_at", { ascending: false })
        .limit(20);
      if (error) throw new Error(error.message);
      return data ?? [];
    },
    refetchInterval: 60_000,
  });
}
